import { Injectable, isDevMode, NgZone } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { DateTime } from 'luxon';
import { BehaviorSubject, firstValueFrom, skipWhile, take } from 'rxjs';
import { PI } from '../constants-types/PI.model';
import { ModuleName } from '../core-data-model';
import { DateFormatUtility } from '../core-utilities/DateFormat.utility';
import { Util } from '../core-utilities/Util';
import { ConfigurationService } from '../system-configuration/Configuration.service';
import { InitDataService } from './InitData/InitData.service';
import { ModuleAccessService } from './ModuleAccess/ModuleAccess.service';
import { UserProfileSettingsService } from './UserProfileSettings/UserProfileSettings.service';

interface LaunchDarklyUserContextBase extends LDClient.LDSingleKindContext {
    kind: 'user';
    key: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    userGuid?: string;
    selectedCompanyId?: string;
    englishLanguageOnly?: boolean;
    userLevelAdmin?: boolean;
    userLevelAccountOwner?: boolean;
    isPI2OrgAdmin?: boolean;
    userLevelPowerUser?: boolean;
    userLevelUser?: boolean;
    userLevelReadOnly?: boolean;
    onboardingEnabled?: boolean;
    cogEnabled?: boolean;
    userLevelPartner?: boolean;
    userCompanyName?: string;
    userCompanyId?: string;
    userWithinDays60?: boolean;
    companyCreateDate?: number;
    hasHire?: boolean;
    hasInspire?: boolean;
    hasEngage?: boolean;
    hasDesign?: boolean;
    onlyHasDiagnose?: boolean;
    hasHire2?: boolean;
    hasDesign2?: boolean;
    hasInspire2?: boolean;
    hasDiagnose2?: boolean;
    isPaidAccount?: boolean;
    hasInspirePaid?: boolean;
    partnerName?: string;
    accountClassificationType?: string;
    upgradeStatus?: string;
    isBornInPI2?: boolean;
    numDaysSinceCompanyCreateDate?: number;
}

type LaunchDarklyUserContext = LaunchDarklyUserContextBase & {
    'user-guid'?: string;
    'selectedCompany-id'?: string;
    'userLevel-admin'?: boolean;
    'userLevel-accountOwner'?: boolean;
    'userLevel-powerUser'?: boolean;
    'userLevel-user'?: boolean;
    'userLevel-readOnly'?: boolean;
    'userLevel-partner'?: boolean;
    'userCompany-name'?: string;
    'userCompany-id'?: string;
    'userWithinDays-60'?: boolean;
    'partner-name'?: string;
};

export type FeatureFlagName = keyof typeof FeatureFlags;

export enum FeatureFlags {
    onboardingChecklist,
    showStrategy,
    wideMenu,
    orgUploadAccess,
    directBehavioralToNewSurveySite,
    useOI3,
    hasTeamDiscovery,
    giveExtraNavigationOptions,
    showShareLink,
    alwaysTrue,
    showPreferredWorkStyle,
    showAlignmentPrompt,
    launchDarklyDemoOn,
    enableSideNavUserManagement,
    hasPaidDesignTwoAccess,
    allowAccessToSubscriptionManagerRoutes,
    allowAccessToBillingManagerRoutes,
    enableVMUsageUserMgmtColumnHire,
    enableVMUsageUserMgmtColumnDesign,
    enableVMUsageUserMgmtColumnDiagnose,
    enableDesignExplainerPages,
    enableHireExplainerPages,
    enableInspireExplainerPages,
    enableDiagnoseExplainerPages,
    enableHideNoAccessToggle,
    enableSharingSideNavDesign,
    enableGlobalNavigation,
    enableDesignExplainerButton,
    enableHireExplainerButton,
    enableDiagnoseExplainerButton,
    logRocketTestingEnabled,
    enableCompanyEmailDomainIndex,
    enableSubscriptionAndBilling,
    enableGlobalAdmin,
    enableInspireProxy,
    enableInspireNavModule,
    enableUserShareTeamDesign,
    enablePlatformApp,
    enableGlobalSearch,
    enableDesign2InGlobalNav,
    enableHire2InGlobalNav,
    enableIntegrationsAuthPage,
    showStandardTeamShareView,
    enableHireJobAccess,
    enableHireTimeTrial,
    globalSearchSlackNotifications,
    enableInspireOrgMgmt,
    enableCustomAvatars,
    enableDesignAvatars,
    enableMyProfile,
    enableMyProfileNameEditing,
    enableMyProfilePronounsEditing,
    enableMyProfilePreferredNameEditing,
    enableHireTimeTrialBanner,
    enableDesign2TimeTrialBanner,
    enableDiagnose2TimeTrialBanner,
    enableInspire2TimeTrialBanner,
    enableHire2TimeTrialGuard,
    enableDesign2TimeTrialGuard,
    enableInspire2VmConsumption,
    enableOrgMgmtBulkInvite,
    internalActivationsUpgradeAccountButton,
    enablePlatformOrgManagement,
    eFactorFeatureFlagEnabled,
    enableSampleDataExperience,
    enableTransferOwnershipJob,
    enableTransferOwnershipTeam,
    enableCategories,
    enablePerformInternalActivation,
    enableQualfiedSalesChatBot,
    enableWorkshopAccess,
    enableAllInspireEmployeeVisibility,
    enableNonCompanyDomainsForInspireDemoAccounts,
    enableMyProfileDownloadBAReport,
    enableFoldersPhase2,
    enableAllowReadOnlyUsersToCreateTeams,
    enableSellerToolsDelete,
    enableCustomInspireInviteEmail,
    enablePerformRedirectForReadOnlyUsers,
    enableUpgradeInProgressPage,
    enablePerformOrgManagement,
    partnerIsLicensee,
    enableSellerExperience,
    enablePI2PaperChecklist,
    enableEmployeePrivacy,
    enableEVPDemo,
    enableAddThirdPartyUsers,
    bulkSendAssessmentsV5,
    coreEmployeeFolderUIHidden,
    enableAdditionalPermissions,
    transferredPeopleGlobalSearchRedirect,
    hideThirdPartyUsersSharing,
    hideGroupAnalyticsNav,
    enableRefactoredBAReport,
    enableDesign2TeamEndpoints,
    enableNewBaGraphs,
    enableEmployeeAssessments,
    enableDownloadCSV,
    enableLanguageSelection,
    enableProspectOnlyFlag,
    enableInspireAssistant,
    enableInspireAssistantFileUpload
}

@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
    private flags = {};
    private overrideFlags = {};
    private launchDarklyClient: LDClient.LDClient | undefined;
    private clientIsLoaded$ = new BehaviorSubject<boolean>(false);
    private hasInitialized = false;
    private hasInitializationStarted = false;
    private initData?: InitDataService; // DO NOT USE THIS. Refactor your code so it is not dependent on the init data directly

    constructor(
        private date: DateFormatUtility,
        private config: ConfigurationService,
        private util: Util,
        private ngZone: NgZone
    ) {
        PI.setFeature = this.setFeature.bind(this);
    }

    get completionPromise(): Promise<boolean> {
        // initialize to false, resolve promise with the first true value
        return firstValueFrom(
            this.clientIsLoaded$.pipe(
                skipWhile((x) => !x),
                take(1)
            )
        );
    }

    /**
     * This should be called by the app-bootstrap-helper service before the app starts
     */
    initLaunchDarkly(
        initData: InitDataService,
        userProfileSetting: UserProfileSettingsService,
        moduleAccess: ModuleAccessService
    ) {
        if (this.hasInitializationStarted) {
            return;
        }

        this.hasInitializationStarted = true;
        this.initData = initData;
        this.setupLaunchDarkly(initData, userProfileSetting, moduleAccess);
    }

    /**
     * @deprecated Switch to using getByStringFlag(featureFlag: string) method instead of taking a reference to the underlying client
     * @returns
     */
    getLaunchDarklyClient() {
        // TECH DEBT: consumers should be using the service and never the client directly!!
        return this.launchDarklyClient;
    }

    /**
     * @deprecated Switch to using the getByFlag(featureFlag: FeatureFlags) method.
     */
    get(featureName: FeatureFlagName) {
        this.initGuard();

        return this.getByFlag(
            FeatureFlags[featureName as keyof typeof FeatureFlags]
        );
    }

    getByFlag(featureFlag: FeatureFlags) {
        this.initGuard();

        const featureName = FeatureFlags[featureFlag];

        if (this.util.hasValue(this.overrideFlags[featureName])) {
            return this.overrideFlags[featureName];
        }

        if (this.hasTeamDiscovery(featureFlag)) {
            return true;
        }

        if (!this.launchDarklyClient) {
            return false;
        }

        return this.launchDarklyClient.variation(featureName, false);
    }

    /**
     * Can be used by clients without needing a direct reference to the launchDarklyClient
     *
     * @param featureFlag
     * @returns the state of the flag
     */
    getByStringFlag(featureFlag: string) {
        this.initGuard();

        if (this.util.hasValue(this.overrideFlags[featureFlag])) {
            return this.overrideFlags[featureFlag];
        }

        if (this.hasTeamDiscovery(FeatureFlags[featureFlag])) {
            return true;
        }

        if (!this.launchDarklyClient) {
            return false;
        }

        return this.launchDarklyClient.variation(featureFlag, false);
    }

    featureFlagValues() {
        return Object.keys(this.flags)
            .map((key) => [key, this.get(key as keyof typeof FeatureFlags)])
            .map((pair) => pair.join(': '))
            .join(', ');
    }

    private hasTeamDiscovery(featureFlag: FeatureFlags) {
        // TECH DEBT: move this to the appropriate client library
        return (
            featureFlag === FeatureFlags.hasTeamDiscovery &&
            this.initData.account.hasTeamDiscovery
        );
    }

    private setFeature(featureName: string, active: boolean) {
        if (isDevMode()) {
            this.ngZone.run(() => {
                this.overrideFlags[featureName] = active;
            });
        }
    }

    private initGuard() {
        if (!this.hasInitialized || !this.launchDarklyClient) {
            throw new Error(
                'LaunchDarkly has to finish initializing before you can use it, have you awaited on the completionPromise?'
            );
        }
    }

    private setupLaunchDarkly(
        initData: InitDataService,
        userProfileSetting: UserProfileSettingsService,
        moduleAccess: ModuleAccessService
    ) {
        const user = this.createLaunchDarklyUserProfile(
            initData,
            userProfileSetting,
            moduleAccess
        );

        const options: LDClient.LDOptions = {
            flushInterval:
                this.config.getLaunchDarklyFlushIntervalInMilliSeconds(),
            bootstrap: 'localStorage',
            sendEventsOnlyForVariation: true
        };

        this.launchDarklyClient = LDClient.initialize(
            this.config.getLaunchDarklyEnvironmentKey(),
            user,
            options
        );

        // Timeout in seconds — prevents the app from hanging indefinitely if LD is unreachable.
        // The localStorage bootstrap will still provide cached flag values.
        this.launchDarklyClient
            .waitForInitialization(5)
            .then(() => {
                this.hasInitialized = true;
                this.clientIsLoaded$.next(true);
            })
            .catch((error: unknown) => {
                // Log the error but still mark as initialized so the app can proceed.
                // All feature flags will default to false via the localStorage bootstrap cache.
                // Note: Logger service requires auth and risks circular deps during early init,
                // so console.error is used intentionally here.
                console.error(
                    'LaunchDarkly failed to initialize, check configuration',
                    error
                );
                this.hasInitialized = true;
                this.clientIsLoaded$.next(true);
            });
    }

    private createLaunchDarklyUserProfile(
        initData: InitDataService,
        userProfileSetting: UserProfileSettingsService,
        moduleAccess: ModuleAccessService
    ): LaunchDarklyUserContext {
        const baseContext: LaunchDarklyUserContextBase = {
            kind: 'user',
            key: initData.user.userGuid,
            firstName: initData.user.firstName,
            lastName: initData.user.lastName,
            email: initData.user.email,
            userGuid: initData.user.userGuid,
            selectedCompanyId: userProfileSetting.currentCompany,
            englishLanguageOnly: this.config.isEnglish(),
            userLevelAdmin: initData.isAdmin(),
            userLevelAccountOwner: initData.isAccountOwner(),
            isPI2OrgAdmin: initData.isPI2OrgAdmin(),
            userLevelPowerUser: initData.isPowerUser(),
            userLevelUser: initData.isUser(),
            userLevelReadOnly: initData.isReadOnly(),
            onboardingEnabled: initData.account.isOnboardingEnabled,
            cogEnabled: userProfileSetting.isCognitiveEnabled(),
            userLevelPartner: initData.user.isNetworkUser,
            userCompanyName: initData.user.company,
            userCompanyId: initData.user.companyId,
            userWithinDays60: !this.date.isXDaysPastDate(
                userProfileSetting.getPresumedStartDate(),
                60
            ),
            companyCreateDate: parseInt(
                DateTime.fromJSDate(
                    initData.account.companyCreateDate
                ).toFormat('yyyyMMdd')
            ),
            hasHire: initData.account.hasHire,
            hasInspire: initData.account.hasInspire,
            hasEngage: initData.account.hasEngage,
            hasDesign: initData.account.hasStrategy,
            onlyHasDiagnose: moduleAccess.isDiagnoseStandalone,
            hasHire2: moduleAccess.hasHireHero,
            hasDesign2: moduleAccess.hasDesign2,
            hasInspire2: moduleAccess.hasInspire2,
            hasDiagnose2: moduleAccess.hasDiagnose2,
            isPaidAccount: moduleAccess.anyModuleIsPaid(),
            hasInspirePaid: moduleAccess.isPaidAccount(ModuleName.Inspire2),
            partnerName: initData.account.partnerCompanyName,
            accountClassificationType: moduleAccess.accountClassificationType,
            upgradeStatus: initData.account.pi2UpgradeStatus,
            isBornInPI2: initData.account.isBornInPI2
        };

        // DO NOT use this pattern elsewhere - it is only here to support properties already using kebab-case
        const context: LaunchDarklyUserContext = {
            ...baseContext,
            'user-guid': baseContext.userGuid,
            'selectedCompany-id': baseContext.selectedCompanyId,
            'userLevel-admin': baseContext.userLevelAdmin,
            'userLevel-accountOwner': baseContext.userLevelAccountOwner,
            'userLevel-powerUser': baseContext.userLevelPowerUser,
            'userLevel-user': baseContext.userLevelUser,
            'userLevel-readOnly': baseContext.userLevelReadOnly,
            'userLevel-partner': baseContext.userLevelPartner,
            'userCompany-name': baseContext.userCompanyName,
            'userCompany-id': baseContext.userCompanyId,
            'userWithinDays-60': baseContext.userWithinDays60,
            'partner-name': baseContext.partnerName
        };

        const daysSinceCreatedDiff = DateTime.local().diff(
            DateTime.fromJSDate(initData.account.companyCreateDate),
            'days'
        );

        if (daysSinceCreatedDiff.isValid) {
            context.numDaysSinceCompanyCreateDate =
                // This returns a float
                Math.floor(daysSinceCreatedDiff.days);
        }

        return context;
    }
}
